type RoleValue = string | {
    name?: string
    title?: string
    value?: string
}

const roleLevel: Record<string, number> = {
    user: 0,
    member: 0,
    lead: 1,
    owner: 1,
    admin: 2,
    sa: 3,
    super_admin: 3,
}

const getRoleName = (role: RoleValue | null | undefined) => {
    if (typeof role === 'string') return role
    return role?.name || role?.title || role?.value || ''
}

export const normalizeRoleName = (role: RoleValue | null | undefined) => (
    getRoleName(role)
        .trim()
        .toLowerCase()
        .replace(/^role[_-]/, '')
        .replace(/[\s-]+/g, '_')
)

const getRoleLevel = (role: RoleValue | null | undefined) => (
    roleLevel[normalizeRoleName(role)]
)

const getCurrentUserRoleLevel = (user: any) => {
    const roles = [
        user?.highest_role,
        user?.role,
        ...(Array.isArray(user?.roles) ? user.roles : []),
        user?.profile?.highest_role,
        user?.profile?.role,
        ...(Array.isArray(user?.profile?.roles) ? user.profile.roles : []),
    ]

    const levels = roles
        .map(getRoleLevel)
        .filter((level): level is number => typeof level === 'number')

    // When the role is absent, fail closed to the base User permissions.
    return levels.length ? Math.max(...levels) : roleLevel.user
}

export const canAssignRole = (role: RoleValue, currentUser: any) => {
    const targetRoleLevel = getRoleLevel(role)
    return typeof targetRoleLevel === 'number' && targetRoleLevel <= getCurrentUserRoleLevel(currentUser)
}

const normalizeIdentifier = (value: unknown) => (
    value === null || value === undefined ? '' : String(value).trim()
)

const normalizeEmail = (value: unknown) => (
    typeof value === 'string' ? value.trim().toLowerCase() : ''
)

export const isCurrentUser = (member: any, currentUser: any) => {
    const currentUserIds = new Set([
        currentUser?.user_id,
        currentUser?.id,
        currentUser?.corid,
        currentUser?.profile?.user_id,
        currentUser?.profile?.id,
        currentUser?.user?.user_id,
        currentUser?.user?.id,
    ].map(normalizeIdentifier).filter(Boolean))
    const memberIds = [
        member?.user_id,
        member?.user?.user_id,
        member?.user?.id,
    ].map(normalizeIdentifier).filter(Boolean)

    if (memberIds.some((id) => currentUserIds.has(id))) {
        return true
    }

    const currentUserEmails = new Set([
        currentUser?.email,
        currentUser?.profile?.email,
        currentUser?.user?.email,
    ].map(normalizeEmail).filter(Boolean))
    const memberEmails = [
        member?.email,
        member?.user?.email,
    ].map(normalizeEmail).filter(Boolean)

    return memberEmails.some((email) => currentUserEmails.has(email))
}

export const canChangeMemberRole = (member: any, newRole: RoleValue, currentUser: any) => {
    if (isCurrentUser(member, currentUser)) {
        return false
    }

    const currentRole = member?.role_in_workspace ?? member?.role

    return canAssignRole(currentRole, currentUser) && canAssignRole(newRole, currentUser)
}

export const filterAssignableRoles = <T extends RoleValue>(roles: T[], currentUser: any) => (
    roles.filter((role) => canAssignRole(role, currentUser))
)
